import Image from "@/components/Image";
import { BuyVipDialog } from "@/components/Vip";
import { openMyCoupon } from "@/utils/applink";
import formatTime from "@/utils/formatTime";
import { getExpireDateTxt } from "@/utils/gaoding";
import { isBasicTypeByCode, isPersonalTypeByCode } from "@/utils/gaodingVip";
import { useGdVipInfo } from "@/utils/hooks/useGdVipInfo";
import {
  gaodingEditHost,
  MATERIAL_DOWNLOAD_RECORD_PAGE,
  VIP_RIGHT_PAGE,
} from "@/utils/host";
import { Button } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useMemo } from "react";
import { useVipCardRedTip } from "../redTipState";
import styles from "./gaoding.module.less";

interface DisplayStrategy {
  hidePanel: boolean;
  hideMaterialVip: boolean;
  hideTemplateVip: boolean;
  showPlusMemberCard: boolean;
}

interface GdVipCardProps {
  displayStrategy?: DisplayStrategy;
}

enum EOrgClass {
  Personal = "personal",
  Org = "org",
}
enum ESeatTypes {
  /** 套版 */
  Gdesign = "gdesign",
  /** 创作 */
  Material = "gdmaterial",
  /** 大会员 */
  Plus = "plus",
}
const getVipCardDesc = (isPersonalOrg, seatType: ESeatTypes) => {
  const orgClass = isPersonalOrg ? EOrgClass.Personal : EOrgClass.Org;
  const VipCardText = {
    [EOrgClass.Personal]: {
      [ESeatTypes.Gdesign]: "模板在线编辑，快速出图",
      [ESeatTypes.Material]: "海量版权图片、免抠元素、PSD 源文件可下载",
      [ESeatTypes.Plus]: "品质模板在线编辑，海量素材源文件下载",
    },
    [EOrgClass.Org]: {
      [ESeatTypes.Gdesign]: "爆款内容轻松共享与复用，企业商用无忧",
      [ESeatTypes.Material]: "共享素材更省心，企业商用无忧",
      [ESeatTypes.Plus]: "爆款内容(模板/素材)轻松共享，企业商用无忧",
    },
  };
  return get(VipCardText, `${orgClass}.${seatType}`);
};

export function GdVipCard({ displayStrategy }: GdVipCardProps = {}) {
  const { data } = useGdVipInfo();

  const { couponTip, onCouponClick } = useVipCardRedTip();

  const {
    code,
    icon,
    title,
    gdesignProductMod: gdesignVip,
    isPlus,
    isMaterialVipExpired,
    materialVipVersion,
    materialVip,
    materialExprieDate,
    gdesignExprieDate,
    productExpireDate,
    isGdesignVipExpired,
  } = data || {};

  // 是否是个人组织
  const isPersonalOrg = isPersonalTypeByCode(code);

  const materialVipBottom = useMemo(() => {
    return (
      <div className={styles.vipBottom}>
        <a
          data-gd-click="button_click"
          data-button-name="下载记录"
          href={MATERIAL_DOWNLOAD_RECORD_PAGE}
          target="_blank"
        >
          <div>下载记录</div>
        </a>

        <a
          onClick={(e) => {
            onCouponClick(e);
            openMyCoupon();
          }}
          data-gd-click="button_click"
          data-button-name="优惠券"
        >
          <div>优惠券</div>
          {couponTip && <span className={styles.redTip}></span>}
        </a>

        <a
          data-gd-click="button_click"
          data-button-name="会员权益"
          href={VIP_RIGHT_PAGE}
          target="_blank"
        >
          <div>会员权益</div>
        </a>
      </div>
    );
  }, [couponTip, onCouponClick]);

  if (!data) return null;

  return (
    <div className={classNames(styles.vips, styles[code])}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Image src={icon} alt="icon" height={28} />
          <div>
            <div>{title}</div>
            <div className={styles.expire}>
              {productExpireDate ? getExpireDateTxt(productExpireDate) : ""}
            </div>
          </div>
        </div>

        <div>
          <a
            className={styles.damspace}
            target="_blank"
            href={
              `https://${gaodingEditHost}` +
              (isPersonalOrg
                ? "/team-space-upgrade?contact_source=HB_ACCOUNT_OVERVIEW_UPGRADE_TEAM"
                : "/dam/teams")
            }
            data-gd-click="button_click"
            data-gd-expose="button_expose"
            data-button-name={isPersonalOrg ? "免费升级为团队" : "团队空间"}
          >
            {isPersonalOrg
              ? `${isBasicTypeByCode(code) ? "" : "免费"}升级为团队`
              : "团队空间"}
          </a>
        </div>
      </div>

      {isPlus ? (
        materialVipBottom
      ) : (
        <>
          {/* 素材会员卡片 - 根据displayStrategy决定是否显示 */}
          {!displayStrategy?.hideMaterialVip && (
            <div className={classNames(styles.card)}>
              {materialVip?.length > 0 ? (
                <>
                  <div className={styles.container}>
                    <div className={styles.title}>素材下载会员</div>
                    {isMaterialVipExpired ? (
                      <>
                        <div className={styles.desc}>
                          会员已于{" "}
                          {formatTime(materialExprieDate, "YYYY-MM-DD")} 到期
                        </div>
                        <div className={styles.right}>
                          <Button
                            data-gd-click="button_click"
                            data-button-name="续费-素材"
                            onClick={() => {
                              BuyVipDialog.open({
                                source: "个人头像下拉框",
                                source_location: "续费",
                              });
                            }}
                          >
                            续费
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.desc}>
                          授权类型: {materialVipVersion?.authText}
                        </div>
                        <div className={styles.right}>
                          <div className={styles.expire}>
                            {getExpireDateTxt(materialExprieDate)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {materialVipBottom}
                </>
              ) : (
                <>
                  <div className={styles.container}>
                    <div className={styles.title}>购买素材会员</div>
                    <div className={styles.desc}>
                      {getVipCardDesc(isPersonalOrg, ESeatTypes.Material)}
                    </div>
                    <div className={styles.right}>
                      <Button
                        data-gd-click="button_click"
                        data-button-name="立即开通-素材"
                        onClick={() => {
                          BuyVipDialog.open({
                            source: "个人头像下拉框",
                            source_location: "立即开通",
                          });
                        }}
                      >
                        立即开通
                      </Button>
                    </div>
                  </div>
                  {isMaterialVipExpired && materialVipBottom}
                </>
              )}
            </div>
          )}

          {/* 模板会员卡片 - 根据displayStrategy决定是否显示 */}
          {!displayStrategy?.hideTemplateVip && (
            <div className={classNames(styles.card)}>
              {gdesignVip && !isGdesignVipExpired ? (
                // 有会员
                <div className={styles.container}>
                  <div className={styles.title}>模板会员</div>
                  <div className={styles.desc}>
                    {getVipCardDesc(isPersonalOrg, ESeatTypes.Gdesign)}
                  </div>
                  <div className={styles.right}>
                    <div className={styles.expire}>
                      {getExpireDateTxt(gdesignExprieDate)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.container}>
                  <div className={styles.title}>购买模板会员</div>
                  <div className={styles.desc}>
                    {getVipCardDesc(isPersonalOrg, ESeatTypes.Gdesign)}
                  </div>
                  <div className={styles.right}>
                    <Button
                      data-gd-click="button_click"
                      data-button-name="立即开通-模板"
                      onClick={() =>
                        window.open(`https://${gaodingEditHost}/pricing`)
                      }
                    >
                      立即开通
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 购买大会员卡片 - 当所有权益都过期时显示 */}
          {displayStrategy?.showPlusMemberCard && (
            <div className={classNames(styles.card, styles.plusMemberCard)}>
              <div className={styles.container}>
                <div className={styles.title}>购买大会员</div>
                <div className={styles.desc}>
                  {getVipCardDesc(isPersonalOrg, ESeatTypes.Plus)}
                </div>
                <div className={styles.right}>
                  <Button
                    data-gd-click="button_click"
                    onClick={() => {
                      BuyVipDialog.open({
                        source: "个人头像下拉框",
                        source_location: "立即开通",
                        buyType: "plus", // 默认选择大会员标签
                      });
                    }}
                  >
                    立即开通
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
